import {
    Box,
    Card,
    CardHeader,
    Divider,
    Flex,
    HelpTip,
    Link,
    Text,
    Heading,
    Switch,
} from "@amzn/storm-ui";
import { isEmpty, startCase } from "lodash";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { isTruthy } from "remeda";

import {
    CONTENT_OWNER_TROUBLESHOOTING_WIKI,
    FORGE_WIKI_URL,
} from "../../constants/links";
import type {
    DimensionMetadata,
    DimensionTemplate,
} from "../../types/dimensions";
import { ErrorPromptDetails } from "../../utils/app.utils";
import { LoadingSpinner } from "../LoadingSpinner";
import { LabelledTextField } from "../common/LabelledTextField";
import { HeaderTemplateDetailField } from "../common/insightDetail.components";
import { DimensionTemplateBuilder } from "../dimensions/DimensionTemplateBuilder";

const PACKAGE_NAME_KEY = "name";
const LAST_UPDATED_TIMESTAMP_KEY = "lastUpdatedTimestamp";

const CARD_HEADER_FIELD_KEYS = new Set([
    PACKAGE_NAME_KEY,
    LAST_UPDATED_TIMESTAMP_KEY,
]);

const HeaderTemplateSingleField: React.FunctionComponent<{
    templateInfo: Record<string, string> | undefined;
    field: string;
    labelOverride?: string;
}> = ({ templateInfo, field, labelOverride }) => {
    if (!templateInfo || !templateInfo[field]) return null;
    return (
        <LabelledTextField
            label={labelOverride ?? startCase(field)}
            value={templateInfo[field]}
        />
    );
};

const FilteredTemplateDetails: React.FunctionComponent<{
    templateInfo: Record<string, string> | undefined;
}> = ({ templateInfo }) => {
    if (!templateInfo) return null;
    const filteredTemplateInfo = Object.entries(templateInfo).filter(
        ([k, v]) => !CARD_HEADER_FIELD_KEYS.has(k) && isTruthy(v),
    );
    if (isEmpty(filteredTemplateInfo)) return null;
    return (
        <>
            {filteredTemplateInfo.map(([key, value]) => (
                <HeaderTemplateDetailField
                    key={key}
                    label={key}
                    value={value}
                />
            ))}
            <Divider />
        </>
    );
};

export interface SettingsCardProps {
    dimensionSections: DimensionTemplate[][];
    loading: boolean;
    dimensionMetadata?: DimensionMetadata;
    templatePublisherPortalLink?: string;
    hydrationPayload?: object;
    templateInfo?: Record<string, string>;
    forgeToggleDisabled?: boolean;
    forgeToggleOn?: boolean;
    onForgeToggleChange?: (toggled: boolean) => void;
}

export const SettingsCard: React.FunctionComponent<SettingsCardProps> = ({
    dimensionSections,
    loading,
    dimensionMetadata,
    templatePublisherPortalLink,
    hydrationPayload,
    templateInfo,
    forgeToggleDisabled = false,
    forgeToggleOn,
    onForgeToggleChange,
}) => {
    return (
        <Card>
            <CardHeader>
                <Flex>
                    <Heading renderAs="h1">Create Insight Package</Heading>
                    {forgeToggleOn !== undefined && (
                        <Box padding="small">
                            <Switch
                                id="forge-toggle"
                                data-testid="forge-toggle"
                                label="Create Interactive Slides"
                                toggled={forgeToggleOn}
                                disabled={forgeToggleDisabled}
                                renderLabelEnd={
                                    <HelpTip
                                        tooltipProps={{
                                            withCloseButton: false,
                                        }}
                                    >
                                        Try insights forge for an interactive
                                        experience. Download data, change time
                                        ranges, translate content, and more.{" "}
                                        <Link
                                            href={FORGE_WIKI_URL}
                                            external
                                            target="_blank"
                                        >
                                            Learn more
                                        </Link>
                                    </HelpTip>
                                }
                                onChange={() => {
                                    onForgeToggleChange?.(!forgeToggleOn);
                                }}
                            />
                        </Box>
                    )}
                </Flex>
                <HeaderTemplateSingleField
                    templateInfo={templateInfo}
                    field={PACKAGE_NAME_KEY}
                    labelOverride="Package Name"
                />
                {templatePublisherPortalLink && (
                    <Text>
                        <Link
                            href={templatePublisherPortalLink}
                            external
                            target="_blank"
                        >
                            Download Template
                        </Link>
                    </Text>
                )}
                <HeaderTemplateSingleField
                    templateInfo={templateInfo}
                    field={LAST_UPDATED_TIMESTAMP_KEY}
                />
            </CardHeader>
            {loading ? (
                <LoadingSpinner />
            ) : (
                <ErrorBoundary
                    fallbackRender={({ error }) => (
                        <ErrorPromptDetails
                            error={error}
                            title="Dimensions failed to render, please contact deck owner"
                            externalLink={CONTENT_OWNER_TROUBLESHOOTING_WIKI}
                        />
                    )}
                >
                    <FilteredTemplateDetails templateInfo={templateInfo} />
                    <DimensionTemplateBuilder
                        key={dimensionSections
                            .flat()
                            .map((d: any) => d.id)
                            .join(",")}
                        dimensionSections={dimensionSections}
                        unusedDimensionIds={["language"]}
                        dimensionMetadata={dimensionMetadata}
                        hydrationPayload={hydrationPayload}
                    />
                </ErrorBoundary>
            )}
        </Card>
    );
};
